import { useState } from 'react'
import { router } from '@inertiajs/react'
import { MessageSquare, Mail, MessageCircle, Smartphone, BookOpen, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { ChannelRow } from './ChannelRow'
import { AddChannelDialog } from './AddChannelDialog'
import { cn } from '@/lib/utils'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import type ChannelSettings from '@/types/serializers/ChannelSettings'
import type Product from '@/types/serializers/Product'

export interface ChannelTypeOption {
  type: string
  label: string
  multiple: boolean
  available: boolean
  can_add: boolean
}

interface Brand {
  id: string
  slug: string
  name: string
}

interface ChannelsViewProps {
  channels: ChannelSettings[]
  channelTypeOptions: ChannelTypeOption[]
  brand: Brand | null
  brands: Product[]
}

const ADD_ICONS: Record<string, typeof MessageSquare> = {
  web_widget: MessageSquare,
  email: Mail,
  whatsapp: MessageCircle,
  messenger: MessageCircle,
  sms: Smartphone,
  help_center: BookOpen,
}

export function ChannelsView({ channels, channelTypeOptions, brand, brands }: ChannelsViewProps) {
  const { withAccount } = useRouteParams()
  const [addType, setAddType] = useState<ChannelTypeOption | null>(null)
  const [eligibleBrands, setEligibleBrands] = useState<Product[]>([])

  const go = (params: Record<string, string | undefined>) => {
    router.get(routes.settings.show.path(withAccount()), params, { preserveScroll: true })
  }

  // Brands that can still take a channel of this type (single-instance types are
  // only offered for brands that don't already have one).
  const brandsForType = (opt: ChannelTypeOption) =>
    opt.multiple ? brands : brands.filter((b) => !channels.some((c) => c.brand_slug === b.slug && c.channel_type === opt.type))

  const onAddClick = (opt: ChannelTypeOption) => {
    const eligible = brandsForType(opt)
    if (eligible.length === 0) return
    if (eligible.length === 1) {
      createChannel(opt.type, eligible[0].slug)
    } else {
      setEligibleBrands(eligible)
      setAddType(opt)
    }
  }

  const createChannel = (type: string, brandSlug: string | null) => {
    setAddType(null)
    router.post(
      routes.settings.createChannel.path(withAccount()),
      { channel_type: type, brand: brandSlug ?? undefined },
      { preserveScroll: true },
    )
  }

  const saveRename = (channel: ChannelSettings, name: string) => {
    if (!name || name === channel.name) return
    router.patch(routes.settings.updateChannel.path(withAccount({ id: channel.id })), {
      channel: { name },
      ...activeBrandParam(),
    }, { preserveScroll: true })
  }

  const toggleActive = (channel: ChannelSettings, active: boolean) => {
    router.patch(routes.settings.updateChannel.path(withAccount({ id: channel.id })), {
      channel: { active },
      ...activeBrandParam(),
    }, { preserveScroll: true })
  }

  const removeChannel = (channel: ChannelSettings) => {
    router.delete(routes.settings.destroyChannel.path(withAccount({ id: channel.id })), {
      data: activeBrandParam(),
      preserveScroll: true,
    })
  }

  const configure = (channel: ChannelSettings) => {
    if (channel.channel_type === 'help_center') {
      go({ tab: 'help-center', brand: channel.brand_slug ?? undefined })
    } else if (channel.channel_type === 'web_widget') {
      go({ tab: 'widget', brand: channel.brand_slug ?? undefined, channel: channel.id })
    }
  }

  return (
    <div className="space-y-8">
      {/* Channels grouped by brand — every brand's channels are visible here. */}
      {brands.map((b) => {
        const brandChannels = channels.filter((c) => c.brand_slug === b.slug)
        return (
          <div key={b.id} className="space-y-2">
            <div className="flex items-center gap-1.5">
              <span
                className="size-2.5 shrink-0 rounded-full border border-border"
                style={{ backgroundColor: b.primary_color || '#10b981' }}
                aria-hidden
              />
              <p className="text-sm font-semibold">{b.name}</p>
            </div>
            {brandChannels.length === 0 ? (
              <p className="text-sm text-muted-foreground">No channels yet — add one below.</p>
            ) : (
              brandChannels.map((channel) => (
                <ChannelRow
                  key={channel.id}
                  channel={channel}
                  onSave={(name) => saveRename(channel, name)}
                  onToggleActive={(active) => toggleActive(channel, active)}
                  onConfigure={() => configure(channel)}
                  onDelete={() => removeChannel(channel)}
                />
              ))
            )}
          </div>
        )
      })}

      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium">Add a channel</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {channelTypeOptions.map((opt) => {
            const Icon = ADD_ICONS[opt.type] ?? MessageSquare
            return (
              <button
                key={opt.type}
                disabled={!opt.can_add}
                onClick={() => opt.can_add && onAddClick(opt)}
                className={cn(
                  'flex items-center justify-between rounded-lg border border-border p-3 text-left disabled:opacity-60',
                  opt.can_add && 'btn-glow-hover cursor-pointer hover:border-primary/50',
                )}
              >
                <span className="flex items-center gap-3">
                  <span className="flex size-9 items-center justify-center rounded-md bg-muted">
                    <Icon className="size-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {opt.multiple ? 'Multiple per brand' : 'One per brand'}
                    </span>
                  </span>
                </span>
                {opt.available ? (
                  opt.can_add ? (
                    <span className="flex items-center gap-1 text-sm text-primary">
                      <Plus className="size-4" /> Add
                    </span>
                  ) : (
                    <Badge variant="outline">Added</Badge>
                  )
                ) : (
                  <Badge variant="outline">Coming soon</Badge>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <AddChannelDialog
        open={addType !== null}
        onOpenChange={(open) => !open && setAddType(null)}
        channelLabel={addType?.label ?? 'channel'}
        brands={eligibleBrands}
        defaultBrandSlug={brand?.slug ?? null}
        onConfirm={(brandSlug) => addType && createChannel(addType.type, brandSlug)}
      />
    </div>
  )
}
